/**
 * Secret Scanner - Detects hardcoded API keys, secrets, and credentials in generated code.
 * Alerts users before they accidentally commit sensitive data.
 */

export interface SecretMatch {
  filePath: string;
  lineNumber: number;
  secretType: string;
  match: string; // Redacted version for display
  severity: "critical" | "warning" | "info";
}

export interface ScanResult {
  hasSecrets: boolean;
  matches: SecretMatch[];
  summary: string;
}

// Patterns for common secret types
const SECRET_PATTERNS: Array<{
  name: string;
  pattern: RegExp;
  severity: "critical" | "warning" | "info";
}> = [
  // API Keys
  {
    name: "OpenAI API Key",
    pattern: /sk-[a-zA-Z0-9]{20,}/g,
    severity: "critical",
  },
  {
    name: "Anthropic API Key",
    pattern: /sk-ant-[a-zA-Z0-9-]{20,}/g,
    severity: "critical",
  },
  {
    name: "Stripe Secret Key",
    pattern: /sk_live_[a-zA-Z0-9]{20,}/g,
    severity: "critical",
  },
  {
    name: "Stripe Publishable Key (Live)",
    pattern: /pk_live_[a-zA-Z0-9]{20,}/g,
    severity: "warning",
  },
  {
    name: "AWS Access Key",
    pattern: /AKIA[A-Z0-9]{16}/g,
    severity: "critical",
  },
  {
    name: "AWS Secret Key",
    pattern: /(?:aws)?_?secret_?(?:access)?_?key['"]?\s*[:=]\s*['"]?[A-Za-z0-9/+=]{40}/gi,
    severity: "critical",
  },
  {
    name: "Google API Key",
    pattern: /AIza[A-Za-z0-9_-]{35}/g,
    severity: "critical",
  },
  {
    name: "GitHub Token",
    pattern: /gh[pousr]_[A-Za-z0-9_]{36,}/g,
    severity: "critical",
  },
  {
    name: "Supabase Service Role Key",
    pattern: /eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9\.[A-Za-z0-9_-]{50,}\.[A-Za-z0-9_-]{20,}/g,
    severity: "critical",
  },
  // Database credentials
  {
    name: "Database URL with Password",
    pattern: /(?:postgres|mysql|mongodb):\/\/[^:]+:[^@]+@[^/]+/gi,
    severity: "critical",
  },
  // Generic patterns
  {
    name: "Private Key",
    pattern: /-----BEGIN (?:RSA |EC )?PRIVATE KEY-----/g,
    severity: "critical",
  },
  {
    name: "Hardcoded Password",
    pattern: /(?:password|passwd|pwd)\s*[:=]\s*['"][^'"]{8,}['"]/gi,
    severity: "warning",
  },
  {
    name: "Bearer Token",
    pattern: /Bearer\s+[A-Za-z0-9_-]{20,}/g,
    severity: "warning",
  },
  {
    name: "Generic API Key Pattern",
    pattern: /(?:api[_-]?key|apikey|api_secret)\s*[:=]\s*['"][A-Za-z0-9_-]{16,}['"]/gi,
    severity: "warning",
  },
];

// Files and patterns to ignore (false positives)
const IGNORE_PATTERNS = [
  /\.env\.example$/,
  /\.env\.sample$/,
  /README\.md$/,
  /SETUP\.md$/,
  // Ignore environment variable references
  /process\.env\./,
  /import\.meta\.env\./,
  /Deno\.env\.get/,
  // Ignore placeholder values
  /your-api-key-here/i,
  /your-key-here/i,
  /xxx+/i,
  /placeholder/i,
];

/**
 * Redact a secret for safe display
 */
function redactSecret(match: string): string {
  if (match.length <= 8) return "****";
  const visibleChars = 4;
  const start = match.slice(0, visibleChars);
  const end = match.slice(-visibleChars);
  return `${start}...${end}`;
}

/**
 * Check if a match should be ignored (false positive)
 */
function shouldIgnore(filePath: string, lineContent: string): boolean {
  // Ignore certain file types
  for (const pattern of IGNORE_PATTERNS) {
    if (typeof pattern === "object" && pattern.test(filePath)) {
      return true;
    }
    if (typeof pattern === "object" && pattern.test(lineContent)) {
      return true;
    }
  }
  return false;
}

/**
 * Scan a single file for secrets
 */
function scanFile(filePath: string, content: string): SecretMatch[] {
  const matches: SecretMatch[] = [];
  const lines = content.split("\n");

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];

    // Skip if line should be ignored
    if (shouldIgnore(filePath, line)) continue;

    for (const { name, pattern, severity } of SECRET_PATTERNS) {
      // Reset regex state
      pattern.lastIndex = 0;

      let match;
      while ((match = pattern.exec(line)) !== null) {
        // Skip if the match looks like a placeholder
        if (shouldIgnore(filePath, match[0])) continue;

        matches.push({
          filePath,
          lineNumber: lineIndex + 1,
          secretType: name,
          match: redactSecret(match[0]),
          severity,
        });
      }
    }
  }

  return matches;
}

/**
 * Scan multiple files for secrets
 */
export function scanForSecrets(
  files: Array<{ name: string; content: string }>
): ScanResult {
  const allMatches: SecretMatch[] = [];

  for (const file of files) {
    // Skip non-code files
    if (
      file.name.endsWith(".png") ||
      file.name.endsWith(".jpg") ||
      file.name.endsWith(".svg") ||
      file.name.endsWith(".ico") ||
      file.name.endsWith(".woff") ||
      file.name.endsWith(".woff2")
    ) {
      continue;
    }

    const fileMatches = scanFile(file.name, file.content);
    allMatches.push(...fileMatches);
  }

  // Deduplicate matches
  const uniqueMatches = allMatches.filter(
    (match, index, self) =>
      index ===
      self.findIndex(
        (m) =>
          m.filePath === match.filePath &&
          m.lineNumber === match.lineNumber &&
          m.secretType === match.secretType
      )
  );

  // Build summary
  const criticalCount = uniqueMatches.filter((m) => m.severity === "critical").length;
  const warningCount = uniqueMatches.filter((m) => m.severity === "warning").length;

  let summary = "";
  if (uniqueMatches.length === 0) {
    summary = "No secrets detected";
  } else if (criticalCount > 0) {
    summary = `Found ${criticalCount} critical secret${criticalCount !== 1 ? "s" : ""} that must be removed`;
  } else {
    summary = `Found ${warningCount} potential secret${warningCount !== 1 ? "s" : ""} to review`;
  }

  return {
    hasSecrets: uniqueMatches.length > 0,
    matches: uniqueMatches,
    summary,
  };
}

/**
 * Quick check if content likely contains secrets (fast pre-filter)
 */
export function likelyContainsSecrets(content: string): boolean {
  // Quick patterns that indicate possible secrets
  const quickPatterns = [
    /sk-[a-zA-Z0-9]{10}/,
    /AKIA[A-Z0-9]{8}/,
    /AIza[A-Za-z0-9_-]{10}/,
    /gh[pousr]_/,
    /-----BEGIN/,
  ];

  return quickPatterns.some((p) => p.test(content));
}

export default scanForSecrets;
